import { AUDIT_ACTION_LABELS, BACKUP_KIND_LABELS, formatBytes } from "@cva/shared";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { AlertTriangle, CheckCircle2, RotateCcw, Send, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Badge, Card, CardHeader, EmptyState, Skeleton } from "../../components/ui/card";
import { client, orpc } from "../../lib/api";
import { cn, errorMessage, formatDateTime, formatRelative } from "../../lib/utils";
import { AdminPageHeader, StatusDot } from "./layout";
import { Pager } from "./users";

// --- Jobs -------------------------------------------------------------------------

export function AdminJobsPage() {
  const queryClient = useQueryClient();
  const jobs = useQuery({ ...orpc.admin.jobs.list.queryOptions(), refetchInterval: 15_000 });
  const refresh = () => queryClient.invalidateQueries({ queryKey: orpc.admin.jobs.key() });

  async function act(kind: "retry" | "remove", queue: string, jobId: string) {
    try {
      if (kind === "retry") await client.admin.jobs.retry({ queue, jobId });
      else await client.admin.jobs.remove({ queue, jobId });
      toast.success(kind === "retry" ? "Job neu gestartet" : "Job entfernt");
      await refresh();
    } catch (error) {
      toast.error(errorMessage(error));
    }
  }

  return (
    <>
      <AdminPageHeader
        title="Jobs"
        description="Vorschaubilder, Exporte, Transkriptionen und Wartung laufen im Worker über diese Warteschlangen."
      />
      {!jobs.data ? (
        <Skeleton className="h-64" />
      ) : (
        <div className="space-y-6">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {jobs.data.queues.map((queue) => (
              <Card key={queue.name} className="p-4">
                <p className="text-sm font-semibold text-zinc-900">{queue.label}</p>
                <p className="mt-2 text-sm text-zinc-600 tabular-nums">
                  {queue.active} aktiv · {queue.waiting} wartend · {queue.delayed} geplant
                </p>
                <p
                  className={cn(
                    "text-xs tabular-nums",
                    queue.failed > 0 ? "text-red-600" : "text-zinc-400",
                  )}
                >
                  {queue.failed} fehlgeschlagen ({queue.failedLastHour} in 1 h)
                </p>
              </Card>
            ))}
          </div>
          <Card>
            <CardHeader
              title="Fehlgeschlagene Jobs"
              description="Fehlgeschlagene Jobs werden nach 7 Tagen automatisch entfernt."
            />
            {jobs.data.failed.length === 0 ? (
              <p className="px-5 py-8 text-center text-sm text-zinc-500">
                Keine fehlgeschlagenen Jobs. 🎉
              </p>
            ) : (
              <ul className="divide-y divide-zinc-100">
                {jobs.data.failed.map((job) => (
                  <li key={`${job.queue}-${job.id}`} className="flex items-start gap-4 px-5 py-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-zinc-900">
                        {job.queue} · {job.name}{" "}
                        <span className="font-normal text-zinc-400">#{job.id.slice(0, 40)}</span>
                      </p>
                      <p className="mt-0.5 text-sm break-words text-red-700">
                        {job.failedReason || "Ohne Fehlermeldung"}
                      </p>
                      <p className="mt-0.5 truncate font-mono text-xs text-zinc-400">{job.data}</p>
                      <p className="text-xs text-zinc-500">
                        {job.attemptsMade} Versuch(e) ·{" "}
                        {job.finishedAt ? formatDateTime(job.finishedAt) : "–"}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => act("retry", job.queue, job.id)}
                      >
                        <RotateCcw /> Erneut
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => act("remove", job.queue, job.id)}
                        aria-label="Entfernen"
                      >
                        <Trash2 />
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      )}
    </>
  );
}

// --- Alerts -----------------------------------------------------------------------

export function AdminAlertsPage() {
  const [status, setStatus] = useState<"open" | "all">("open");
  const alerts = useQuery({
    ...orpc.admin.alerts.list.queryOptions({ input: { status, limit: 200 } }),
    refetchInterval: 30_000,
  });
  const overview = useQuery(orpc.admin.overview.queryOptions());
  const [sending, setSending] = useState(false);
  const channels = overview.data?.notifications;

  return (
    <>
      <AdminPageHeader
        title="Warnungen"
        description="Die Regeln laufen jede Minute. Neue, eskalierte und behobene Warnungen werden gemeldet; offene werden erinnert (kritisch alle 6 h, sonst täglich)."
        actions={
          <Button
            variant="secondary"
            loading={sending}
            onClick={async () => {
              setSending(true);
              try {
                const result = await client.admin.alerts.test();
                if (result.channels.length === 0) {
                  toast.warning(
                    "Kein Kanal hat die Nachricht zugestellt. SMTP oder ALERT_WEBHOOK_URL einrichten.",
                  );
                } else {
                  toast.success(`Gesendet über: ${result.channels.join(", ")}`);
                }
              } catch (error) {
                toast.error(errorMessage(error));
              } finally {
                setSending(false);
              }
            }}
          >
            <Send /> Test-Benachrichtigung
          </Button>
        }
      />
      {channels ? (
        <div className="mb-6 flex flex-wrap gap-2 text-sm">
          <Badge tone={channels.smtp ? "success" : "neutral"}>
            E-Mail {channels.smtp ? `an ${channels.emailRecipients} Empfänger` : "aus (kein SMTP)"}
          </Badge>
          <Badge tone={channels.webhook ? "success" : "neutral"}>
            Webhook {channels.webhook ? "an" : "aus"}
          </Badge>
          <Badge tone={channels.heartbeat ? "success" : "neutral"}>
            Dead-Man's-Switch {channels.heartbeat ? "an" : "aus"}
          </Badge>
          <Badge tone="success">Uptime-Check (GitHub) stündlich</Badge>
        </div>
      ) : null}
      <div className="mb-4 flex gap-1">
        {(["open", "all"] as const).map((value) => (
          <Button
            key={value}
            size="sm"
            variant={status === value ? "primary" : "secondary"}
            onClick={() => setStatus(value)}
          >
            {value === "open" ? "Offen" : "Alle"}
          </Button>
        ))}
      </div>
      {!alerts.data ? (
        <Skeleton className="h-40" />
      ) : alerts.data.length === 0 ? (
        <EmptyState
          icon={<CheckCircle2 />}
          title={status === "open" ? "Keine offenen Warnungen" : "Noch keine Warnungen"}
          description="Festplatte, Speicher, Warteschlangen, Fehler, Backups und alle Dienste werden jede Minute geprüft."
        />
      ) : (
        <Card>
          <ul className="divide-y divide-zinc-100">
            {alerts.data.map((alert) => (
              <li key={alert.id} className="flex items-start gap-3 px-5 py-3">
                {alert.status === "resolved" ? (
                  <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-emerald-600" />
                ) : (
                  <AlertTriangle
                    className={cn(
                      "mt-0.5 size-4 shrink-0",
                      alert.severity === "critical" ? "text-red-600" : "text-amber-500",
                    )}
                  />
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-zinc-900">
                    {alert.title}{" "}
                    <Badge
                      tone={alert.severity === "critical" ? "danger" : "warning"}
                      className="ml-1"
                    >
                      {alert.severity === "critical" ? "Kritisch" : "Warnung"}
                    </Badge>
                  </p>
                  <p className="text-sm text-zinc-600">{alert.message}</p>
                  <p className="text-xs text-zinc-400">
                    {formatDateTime(alert.openedAt)}
                    {alert.resolvedAt
                      ? ` – behoben ${formatDateTime(alert.resolvedAt)}`
                      : ` · offen seit ${formatRelative(alert.openedAt)}`}
                    {` · ${alert.notifyCount}× gemeldet`}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </>
  );
}

// --- Backups ----------------------------------------------------------------------

function Code({ children }: { children: string }) {
  return (
    <pre className="overflow-x-auto rounded-lg bg-zinc-900 px-4 py-3 text-xs text-zinc-100">
      {children}
    </pre>
  );
}

export function AdminBackupsPage() {
  const runs = useQuery(orpc.admin.backups.list.queryOptions({ input: { limit: 200 } }));
  const overview = useQuery(orpc.admin.overview.queryOptions());
  const backups = overview.data?.backups;
  const service = overview.data?.services.find((s) => s.id === "backup");

  return (
    <>
      <AdminPageHeader
        title="Backups"
        description="Der backup-Container sichert die Datenbank täglich, testet wöchentlich die Wiederherstellung und kopiert – sobald eingerichtet – Datenbank, Medien und Konfiguration verschlüsselt auf einen zweiten Speicherort."
      />
      {service ? (
        <Card className="mb-6 flex items-center gap-3 p-4">
          <StatusDot level={service.status} />
          <span className="text-sm text-zinc-800">{service.detail}</span>
        </Card>
      ) : null}
      {backups && !backups.lastOffsite ? (
        <Card className="mb-6 border-amber-200 bg-amber-50 p-5 text-sm text-amber-900">
          <p className="font-semibold">Off-Site-Kopie ist noch nicht eingerichtet</p>
          <p className="mt-1">
            Solange liegen alle Sicherungen auf demselben Server wie die Daten. Für einen zweiten
            Speicherort (z. B. Hetzner Storage Box, Backblaze B2 oder S3) eine Datei{" "}
            <code>backup.env</code> neben die <code>.env</code> der Umgebung legen und den Stack neu
            starten:
          </p>
          <div className="mt-3">
            <Code>{`RESTIC_REPOSITORY=s3:https://<endpoint>/<bucket>/cva-prod
RESTIC_PASSWORD=<langes Passwort – zusätzlich im Passwort-Manager ablegen!>
AWS_ACCESS_KEY_ID=...
AWS_SECRET_ACCESS_KEY=...`}</Code>
          </div>
        </Card>
      ) : null}
      <Card className="mb-6">
        <CardHeader title="Verlauf" />
        {!runs.data ? (
          <Skeleton className="m-5 h-40" />
        ) : runs.data.length === 0 ? (
          <p className="px-5 py-8 text-center text-sm text-zinc-500">
            Noch keine Backups protokolliert. Der backup-Container sichert kurz nach dem Start zum
            ersten Mal.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-zinc-100 text-left text-xs text-zinc-500">
              <tr>
                <th className="px-5 py-2.5 font-medium">Art</th>
                <th className="px-3 py-2.5 font-medium">Ergebnis</th>
                <th className="px-3 py-2.5 font-medium">Zeitpunkt</th>
                <th className="px-3 py-2.5 text-right font-medium">Größe</th>
                <th className="px-5 py-2.5 font-medium">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {runs.data.map((run) => (
                <tr key={run.id}>
                  <td className="px-5 py-2.5 whitespace-nowrap">{BACKUP_KIND_LABELS[run.kind]}</td>
                  <td className="px-3 py-2.5">
                    <Badge tone={run.status === "ok" ? "success" : "danger"}>
                      {run.status === "ok" ? "OK" : "Fehler"}
                    </Badge>
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-zinc-600">
                    {formatDateTime(run.finishedAt)}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums">
                    {run.sizeBytes ? formatBytes(run.sizeBytes) : "–"}
                  </td>
                  <td
                    className="max-w-md truncate px-5 py-2.5 text-xs text-zinc-500"
                    title={run.message ?? run.file ?? ""}
                  >
                    {run.message ?? run.file ?? ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
      <Card>
        <CardHeader
          title="Befehle auf dem Server"
          description="Im Ordner der Umgebung ausführen."
        />
        <div className="space-y-3 p-5 text-sm text-zinc-700">
          <p>Jetzt sichern (Datenbank + Off-Site):</p>
          <Code>./backup.sh</Code>
          <p>Wiederherstellung testen / Übersicht:</p>
          <Code>{"./backup.sh verify\n./backup.sh status"}</Code>
          <p>Datenbank aus einem Dump zurückspielen:</p>
          <Code>./restore-db.sh backups/db-daily-JJJJMMTT-HHMMSS.dump</Code>
          <p>Medien und Konfiguration aus der Off-Site-Kopie holen:</p>
          <Code>docker compose exec backup cva-backup restore-files latest /restore</Code>
        </div>
      </Card>
    </>
  );
}

// --- Audit log --------------------------------------------------------------------

export function AdminAuditPage() {
  const [offset, setOffset] = useState(0);
  const audit = useQuery({
    ...orpc.admin.audit.list.queryOptions({ input: { limit: 50, offset } }),
    placeholderData: keepPreviousData,
  });
  return (
    <>
      <AdminPageHeader
        title="Audit-Log"
        description="Jede Aktion im Admin-Bereich, jedes Einsehen eines Kontos oder einer Organisation und jeder Server-Befehl. Einträge werden nach zwölf Monaten gelöscht."
      />
      <Card>
        {!audit.data ? (
          <Skeleton className="m-5 h-40" />
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-zinc-100 text-left text-xs text-zinc-500">
              <tr>
                <th className="px-5 py-2.5 font-medium">Zeitpunkt</th>
                <th className="px-3 py-2.5 font-medium">Wer</th>
                <th className="px-3 py-2.5 font-medium">Aktion</th>
                <th className="px-3 py-2.5 font-medium">Ziel</th>
                <th className="px-5 py-2.5 font-medium">IP</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {audit.data.items.map((entry) => (
                <tr key={entry.id} className="align-top">
                  <td className="px-5 py-2.5 whitespace-nowrap text-zinc-600">
                    {formatDateTime(entry.createdAt)}
                  </td>
                  <td className="px-3 py-2.5">{entry.actorEmail}</td>
                  <td className="px-3 py-2.5">
                    {AUDIT_ACTION_LABELS[entry.action] ?? entry.action}
                    {Object.keys(entry.details).length > 0 ? (
                      <span
                        className="block max-w-sm truncate font-mono text-xs text-zinc-400"
                        title={JSON.stringify(entry.details)}
                      >
                        {JSON.stringify(entry.details)}
                      </span>
                    ) : null}
                  </td>
                  <td className="px-3 py-2.5">
                    {entry.targetType === "user" && entry.targetId ? (
                      <Link
                        to="/admin/users/$userId"
                        params={{ userId: entry.targetId }}
                        className="text-brand-700 hover:underline"
                      >
                        Konto
                      </Link>
                    ) : entry.targetType === "org" && entry.targetId ? (
                      <Link
                        to="/admin/orgs/$orgId"
                        params={{ orgId: entry.targetId }}
                        className="text-brand-700 hover:underline"
                      >
                        Organisation
                      </Link>
                    ) : (
                      <span className="text-zinc-500">{entry.targetType ?? "–"}</span>
                    )}
                  </td>
                  <td className="px-5 py-2.5 text-xs text-zinc-500">{entry.ip ?? "–"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
      <Pager offset={offset} total={audit.data?.total ?? 0} onChange={setOffset} />
    </>
  );
}
